import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import MainHeader from "../../shared/MainHeader";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { GET } from "../../api/ApiService";
import EncryptionHelper from "../../shared/EncryptionHelper";
import { useSelector } from "react-redux";

const DashboardPage = () => {
  const userDetails = useSelector((state) => state.userReducer.userDetails);
  const localizer = momentLocalizer(moment);

  const [events, setEvents] = useState([]);
  const [data, setData] = useState([]);
  const [casetype, setCasetype] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(
          `case/dashboard-data?userid=${userDetails?.id}&status=Open`
        );

        const encryptedData = response?.encryptedData;
        const decryptedData = EncryptionHelper.decryptData(encryptedData);
        console.log("Decrypted dashboard data =>", decryptedData);

        // Extract unique case types
        const case_types = [];
        decryptedData.forEach((element) => {
          if (element?.CaseType && !case_types.includes(element.CaseType)) {
            case_types.push(element.CaseType);
          }
        });

        setCasetype(case_types);
        setData(decryptedData);

        // ✅ Combine both ReportDueDate and AdjournedDate into events
        const eventList = decryptedData.flatMap((item) => {
          const eventsForItem = [];

          if (item.ReportDueDate) {
            const reportDueDate = new Date(`${item.ReportDueDate}T09:00:00`);
            const endDate = new Date(reportDueDate.getTime() + 60 * 60 * 1000);
            eventsForItem.push({
              title: `Report Due: ${item.CaseName}`,
              start: reportDueDate,
              end: endDate,
              type: "report",
            });
          }

          if (item.AdjournedDate) {
            const adjournedDate = new Date(`${item.AdjournedDate}T09:00:00`);
            const endDate = new Date(adjournedDate.getTime() + 60 * 60 * 1000);
            eventsForItem.push({
              title: `Adjourned Date: ${item.CaseName}`,
              start: adjournedDate,
              end: endDate,
              type: "adjourned",
            });
          }

          return eventsForItem;
        });

        setEvents(eventList);
      } catch (err) {
        console.warn("Error fetching dashboard data:", err);
      }
    };

    fetchData();
  }, [userDetails?.id]);

  // Format event title with readable date
  const eventTitleFormatter = (event) => {
    const formattedDate = moment(event.start).format("MMMM Do YYYY");
    return `${event.title} (${formattedDate})`;
  };

  return (
    <Box>
      <MainHeader headingText="User Report Due Case Calendar" />
      <Stack
        p={4}
        gap={4}
        sx={{
          border: "1px solid #C1C1C1",
          marginBottom: "20px",
        }}
      >
        {/* Header Stats */}
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          pl={2}
          pr={2}
        >
          <Stack direction={"row"} alignItems={"center"} gap={2}>
            <Typography>Total Cases:</Typography>
            <Typography>{data?.length}</Typography>
          </Stack>

          <Stack direction={"row"} alignItems={"center"} gap={2}>
            <Typography>Case Types:</Typography>
            <Typography>
              {casetype?.map((item, index) => (
                <span key={index}>
                  {index === casetype.length - 1 && casetype.length > 1
                    ? "and "
                    : ""}
                  {item}
                  {index < casetype.length - 2 ? ", " : " "}
                </span>
              ))}
            </Typography>
          </Stack>
        </Stack>

        {/* Legend */}
        <Stack
          direction="row"
          alignItems="center"
          gap={3}
          pl={2}
          pr={2}
          flexWrap="wrap"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#1447E6",
                borderRadius: "3px",
              }}
            />
            <Typography fontSize={14}>Report Due</Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#E67E22",
                borderRadius: "3px",
              }}
            />
            <Typography fontSize={14}>Adjourned Date</Typography>
          </Stack>
        </Stack>

        {/* Calendar */}
        <div style={{ height: 500 }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ margin: "0 2px" }}
            components={{
              event: ({ event }) => (
                <div style={{ whiteSpace: "normal", lineHeight: "1.2em" }}>
                  {eventTitleFormatter(event)}
                </div>
              ),
            }}
            eventPropGetter={(event) => {
              let backgroundColor = "#1447E6"; // default for Report Due
              if (event.type === "adjourned") backgroundColor = "#E67E22"; // orange for Adjourned Date

              return {
                style: {
                  backgroundColor,
                  color: "#fff",
                  borderRadius: "6px",
                  padding: "4px 6px",
                  fontSize: "12px",
                  whiteSpace: "normal",
                  overflow: "visible",
                  lineHeight: "1.3em",
                },
              };
            }}
          />
        </div>
      </Stack>
    </Box>
  );
};

export default DashboardPage;
